import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, FolderOpen, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import Button from '../../ui/Button';
import FolderCard from './FolderCard';
import { staggerChildren } from '../../../utils/animations';
import { haptics } from '../../../utils/haptics';

const FOLDER_BATCH_SIZE = 50;
const FOLDER_CACHE_TTL_MS = 30 * 60 * 1000;
// Above this count the folder strip becomes scrollable with bounded height so a
// user with hundreds of folders doesn't get a page-long wall of folder chips.
const SCROLLABLE_THRESHOLD = 40;

const folderCacheByContext = new Map();

function readCache(folderId) {
  const row = folderCacheByContext.get(folderId);
  if (!row) return null;
  if (Date.now() - row.fetched_at > FOLDER_CACHE_TTL_MS) return null;
  return row;
}

function writeCache(folderId, payload) {
  folderCacheByContext.set(folderId, { ...payload, fetched_at: Date.now() });
}

/**
 * Folder Section
 *
 * Renders the subfolders for the current folder context as its own section,
 * above the (designs-only) grid. Independent of design pagination — the
 * folders stay visible whichever design page is showing.
 *
 * Many-folders handling: the n8n fetch-folders endpoint returns at most
 * FOLDER_BATCH_SIZE per call with a continuation token. We render all loaded
 * folders, expose "Show more folders" when more are available, and bound the
 * section height with a scrollable area once the count is large.
 *
 * Caching: 30-min client-side per folderId. The Dashboard's Refresh Designs
 * button (and the Sync-Now follow-up) forces a refetch by bumping the
 * `refreshKey` prop.
 */
function FolderSection({ folderId, refreshKey = 0, onEnterFolder }) {
  const [folders, setFolders] = useState([]);
  const [continuation, setContinuation] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const lastRefreshKeyRef = useRef(refreshKey);
  const inflightRef = useRef(null);

  const loadInitial = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      const cached = readCache(folderId);
      if (cached) {
        setFolders(cached.folders);
        setContinuation(cached.continuation);
        setHasMore(cached.hasMore);
        setError(null);
        return;
      }
    }
    if (inflightRef.current) return inflightRef.current;
    setIsInitialLoading(true);
    setError(null);
    const inflight = (async () => {
      try {
        const result = await api.fetchFolders({ folderId, limit: FOLDER_BATCH_SIZE });
        setFolders(result.folders);
        setContinuation(result.continuation);
        setHasMore(result.hasMore);
        writeCache(folderId, {
          folders: result.folders,
          continuation: result.continuation,
          hasMore: result.hasMore,
        });
      } catch (err) {
        console.error('Failed to fetch folders:', err);
        setError(err.message || 'Failed to load folders');
        setFolders([]);
        setContinuation(null);
        setHasMore(false);
      } finally {
        setIsInitialLoading(false);
        inflightRef.current = null;
      }
    })();
    inflightRef.current = inflight;
    return inflight;
  }, [folderId]);

  // Initial load + reset when folder context changes
  useEffect(() => {
    setFolders([]);
    setContinuation(null);
    setHasMore(false);
    setError(null);
    loadInitial();
  }, [folderId, loadInitial]);

  // Force-refresh when parent bumps refreshKey
  useEffect(() => {
    if (refreshKey === lastRefreshKeyRef.current) return;
    lastRefreshKeyRef.current = refreshKey;
    loadInitial({ force: true });
  }, [refreshKey, loadInitial]);

  const loadMore = useCallback(async () => {
    if (!continuation || isLoadingMore) return;
    haptics.light();
    setIsLoadingMore(true);
    try {
      const result = await api.fetchFolders({
        folderId,
        continuation,
        limit: FOLDER_BATCH_SIZE,
      });
      const nextFolders = [...folders, ...result.folders];
      setFolders(nextFolders);
      setContinuation(result.continuation);
      setHasMore(result.hasMore);
      writeCache(folderId, {
        folders: nextFolders,
        continuation: result.continuation,
        hasMore: result.hasMore,
      });
    } catch (err) {
      console.error('Failed to load more folders:', err);
      setError(err.message || 'Failed to load more folders');
    } finally {
      setIsLoadingMore(false);
    }
  }, [continuation, isLoadingMore, folderId, folders]);

  // Show nothing if no folders and not loading and no error.
  if (!isInitialLoading && folders.length === 0 && !error) {
    return null;
  }

  const countLabel = (() => {
    if (isInitialLoading && folders.length === 0) return 'Loading folders…';
    const base = `${folders.length} folder${folders.length === 1 ? '' : 's'}`;
    return hasMore ? `${base} · more available` : base;
  })();

  const isScrollable = folders.length > SCROLLABLE_THRESHOLD;

  return (
    <section
      className="mb-4"
      data-testid="folder-section"
      aria-label="Folders"
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsCollapsed(c => !c)}
          className="inline-flex items-center gap-2 text-foreground hover:text-foreground-secondary"
          aria-expanded={!isCollapsed}
          aria-controls="folder-section-body"
        >
          <FolderOpen className="w-4 h-4 text-primary-600 dark:text-primary-300" />
          <span className="text-body-sm text-foreground-tertiary" data-testid="folder-count">
            {countLabel}
          </span>
          {isCollapsed
            ? <ChevronDown className="w-4 h-4 text-foreground-tertiary" />
            : <ChevronUp className="w-4 h-4 text-foreground-tertiary" />}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            id="folder-section-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ overflow: 'hidden' }}
          >
            {error && (
              <div
                className="mb-3 p-3 squircle-sm bg-red-500/10 border border-red-500/30 text-body-sm text-red-700 dark:text-red-200"
                role="alert"
              >
                {error}
              </div>
            )}

            <div
              className={
                isScrollable
                  ? 'max-h-[12rem] overflow-y-auto pr-1 -mr-1'
                  : ''
              }
            >
              <motion.div
                variants={staggerChildren}
                initial="initial"
                animate="animate"
                className="flex flex-wrap gap-2"
                data-testid="folder-grid"
              >
                {folders.map((folder) => (
                  <motion.div
                    key={`folder:${folder.folder_id}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  >
                    <FolderCard folder={folder} onEnter={onEnterFolder} />
                  </motion.div>
                ))}
                {isInitialLoading && folders.length === 0 && (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={`skeleton:${i}`}
                      className="h-8 w-32 rounded-full shimmer bg-foreground/5"
                      data-testid="folder-skeleton"
                    />
                  ))
                )}
              </motion.div>
            </div>

            {hasMore && (
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  variant="secondary"
                  size="sm"
                  data-testid="folder-load-more"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    'Show more folders'
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default memo(FolderSection);
